import { ComponentProps, ReactNode, forwardRef, useId, useRef } from 'react';

import { Root as RadioGroupRoot } from '@radix-ui/react-radio-group';
import type { RadioGroupProps as PrimitiveGroupProps } from '@radix-ui/react-radio-group';

import { styledStackRecipe } from './RadioGroup.css';
import { useInputIds } from '../../../hooks/useInputIds';
import { useValidationErrorEvent } from '../../../hooks/useValidationErrorEvent';
import { useI18nTranslation } from '../../../providers/i18n';
import { mergeRefs } from '../../../util';
import { Error as ErrorComponent } from '../../Input/InputError';
import { Hint } from '../../Input/InputHint';
import { Stack } from '../../Stack/Stack';
import { Text } from '../../Text/Text';
import { RadioGroupContext } from '../RadioGroupContext';

export { useRadioGroup } from '../RadioGroupContext';

type RadioGroupBaseProps = {
  children: ReactNode;
  name: string;
  hint?: ReactNode;
  error?: string;
  isOptional?: boolean;
};

type RootProps = Pick<PrimitiveGroupProps, 'defaultValue' | 'value' | 'onValueChange'>;
type StackProps = Pick<ComponentProps<typeof Stack>, 'direction' | 'wrap' | 'alignY'> & {
  isFullWidth?: boolean;
};

type LabelProps =
  | {
      label: string;
      'aria-labelledby'?: never;
    }
  | {
      'aria-labelledby': string;
      label?: never;
    };

type RadioGroupProps = RadioGroupBaseProps & RootProps & StackProps & LabelProps;

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      children,
      label,
      name,
      value,
      onValueChange,
      defaultValue,
      error,
      hint,
      isOptional,
      direction = 'row',
      wrap = true,
      alignY,
      'aria-labelledby': ariaLabelledby,
      isFullWidth = false,
    },
    ref,
  ) => {
    const { errorId, hintId, inputId } = useInputIds({ error, hint });
    const groupLabelId = useId();
    const i18nOptionalText = useI18nTranslation('optional');

    const localRef = useRef<HTMLDivElement>(null);
    // Merge the local ref and forwarded ref so we can forward it *and* use it locally
    const mergedRef = mergeRefs([ref, localRef]);

    useValidationErrorEvent({ error, name }, localRef);

    return (
      <RadioGroupRoot
        ref={mergedRef}
        name={name}
        onValueChange={onValueChange}
        value={value}
        defaultValue={defaultValue}
        required={!isOptional}
        aria-labelledby={ariaLabelledby || groupLabelId}
        id={inputId}
        orientation={direction === 'row' ? 'horizontal' : 'vertical'}
        aria-describedby={hintId}
        loop
        style={{ width: isFullWidth ? '100%' : undefined }}>
        <RadioGroupContext.Provider value={{ ariaDescribedby: errorId }}>
          <Stack className={styledStackRecipe({ isFullWidth })} gap="3">
            {label && (
              <Text size="BodyS" weight="bold" id={groupLabelId}>
                {label}
                {isOptional ? <span>{` (${i18nOptionalText})`}</span> : null}
              </Text>
            )}
            <Stack
              className={styledStackRecipe({ isFullWidth })}
              direction={direction}
              gap="3"
              wrap={wrap}
              alignY={alignY}>
              {children}
            </Stack>
            {error ? (
              <ErrorComponent id={errorId} hasArrow={false}>
                {error}
              </ErrorComponent>
            ) : null}
            {hint ? <Hint id={hintId}>{hint}</Hint> : null}
          </Stack>
        </RadioGroupContext.Provider>
      </RadioGroupRoot>
    );
  },
);
